/**
 * The public demo logins, shown WITHOUT waiting for the API.
 *
 * The home page and the sign-in screen used to render their demo panels only
 * after `GET /public/demo` answered. On Render's free tier the first request
 * after a quiet spell waits ~50 s for the API to boot, so the panel simply was
 * not there when a visitor looked (owner's report, 2 Oct 2026) — and the demo
 * is what most visitors came for.
 *
 * So the panel now draws immediately from these constants (twins of
 * apps/api/src/services/demo.service.ts — keep the emails and password in
 * step), and the API's answer only REMOVES it, when the server says the demo
 * was never seeded. The last answer is remembered per browser, so a fresh
 * install that has no demo stops flashing the panel after the first visit.
 */

import { useEffect, useState } from 'react';
import { platform } from './api';

const DEMO_PASSWORD = 'demo@2026';

export const DEMO_ACCOUNTS = [
  {
    role: 'admin',
    label: 'Committee',
    blurb: 'Roster, dues, events, results, volunteering — and the portal itself.',
    email: 'demo.committee@runnercrew.app',
    password: DEMO_PASSWORD,
  },
  {
    role: 'member',
    label: 'Runner',
    blurb: 'A member’s own bib, personal bests, events and payment history.',
    email: 'demo.runner@runnercrew.app',
    password: DEMO_PASSWORD,
  },
];

const KEY = 'rc_demo_enabled';

function remembered() {
  try {
    return localStorage.getItem(KEY) !== '0';
  } catch {
    return true;
  }
}

/** The demo accounts to show right now; corrected once the API answers. */
export function useDemoAccounts() {
  const [accounts, setAccounts] = useState(() => (remembered() ? DEMO_ACCOUNTS : []));

  useEffect(() => {
    let alive = true;
    platform
      .demo()
      .then((r) => {
        const live = r.accounts ?? [];
        try {
          localStorage.setItem(KEY, live.length ? '1' : '0');
        } catch {
          /* private window — the constants still render */
        }
        if (alive) setAccounts(live);
      })
      .catch(() => {}); // API asleep or offline: keep what is on screen
    return () => {
      alive = false;
    };
  }, []);

  return accounts;
}
